'use client'

import { type RefObject, useCallback, useEffect, useRef } from 'react'

// A hover heading for the detail panel is held back until the pointer has
// been still (or off course) this long, so rows crossed on the way there
// don't steal the cursor.
const HOVER_DELAY_MS = 100
// Horizontal travel before a move counts as "moving right" at all.
const MIN_TRAVEL_PX = 8
// Steepest run (vertical per horizontal px) that still counts as heading for
// the panel; anything steeper is browsing down the list with some drift.
const MAX_STEEPNESS = 2
// Inside this distance of the panel's near edge, any steepness counts — the
// pointer is about to cross into the panel.
const EDGE_ZONE_PX = 120
// Recent pointer samples the current direction is judged over, so a turn
// inside the triangle (left, or straight down) is caught on the move.
const TRAIL_LENGTH = 3
// Vertical slack on the triangle's two far corners.
const CORNER_PAD_PX = 50

interface Point {
  x: number
  y: number
}

interface PanelRect {
  left: number
  top: number
  bottom: number
}

const slope = (from: Point, to: Point): number => (to.y - from.y) / (to.x - from.x)

/**
 * True when the pointer, having last committed a row at `start`, is now at
 * `position` inside the triangle spanned by `start` and the panel's near edge
 * (padded vertically) — i.e. it is on its way into the panel.
 */
export function isMovingTowardPanel(start: Point, position: Point, rect: PanelRect): boolean {
  const dx = position.x - start.x
  const dy = Math.abs(position.y - start.y)
  if (dx < MIN_TRAVEL_PX) return false

  const nearEdge = rect.left - position.x <= EDGE_ZONE_PX
  if (dy > dx * MAX_STEEPNESS && !nearEdge) return false

  const topCorner = { x: rect.left, y: rect.top - CORNER_PAD_PX }
  const bottomCorner = { x: rect.left, y: rect.bottom + CORNER_PAD_PX }
  return (
    slope(position, topCorner) < slope(start, topCorner) &&
    slope(position, bottomCorner) > slope(start, bottomCorner)
  )
}

export interface HoverIntent {
  /** Call from a row's onMouseEnter. */
  onRowEnter: (index: number, event: { clientX: number; clientY: number }) => void
  /** Call from the list's onPointerMove. */
  onListPointerMove: (event: { clientX: number; clientY: number }) => void
  /** Call when the pointer leaves the list, or a keyboard move takes over. */
  cancel: () => void
}

/**
 * Safe-triangle hover intent for a list → detail-panel pair.
 *
 * A plain mouseenter handler re-targets every row the pointer crosses on its
 * way to the panel. This holds a hover back while the pointer is inside the
 * triangle spanned by its last committed position and the panel's near edge,
 * so a diagonal run at the panel keeps the row it started from. Every move
 * that is still on course restarts the wait, so a slow run that lingers in a
 * crossed row doesn't commit it either; stopping or turning away does.
 */
export function useHoverIntent(
  panelRef: RefObject<HTMLElement | null>,
  onHover: (index: number) => void
): HoverIntent {
  const anchorRef = useRef<Point | null>(null)
  const trailRef = useRef<Point[]>([])
  const pendingRef = useRef<{ index: number; position: Point } | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onHoverRef = useRef(onHover)

  useEffect(() => {
    onHoverRef.current = onHover
  }, [onHover])

  const clearTimer = useCallback(() => {
    if (timerRef.current === null) return
    clearTimeout(timerRef.current)
    timerRef.current = null
  }, [])

  const cancel = useCallback(() => {
    clearTimer()
    pendingRef.current = null
    anchorRef.current = null
    trailRef.current = []
  }, [clearTimer])

  useEffect(() => clearTimer, [clearTimer])

  const commit = useCallback(
    (index: number, position: Point) => {
      clearTimer()
      pendingRef.current = null
      anchorRef.current = position
      onHoverRef.current(index)
    },
    [clearTimer]
  )

  const isOnCourse = useCallback(
    (position: Point): boolean => {
      const start = anchorRef.current
      const rect = panelRef.current?.getBoundingClientRect()
      if (!start || !rect || rect.width === 0) return false
      // Still travelling right right now, not just somewhere inside the triangle.
      const recent = trailRef.current[0]
      if (recent && position.x <= recent.x) return false
      return isMovingTowardPanel(start, position, rect)
    },
    [panelRef]
  )

  const armTimer = useCallback(() => {
    clearTimer()
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      const pending = pendingRef.current
      if (pending) commit(pending.index, pending.position)
    }, HOVER_DELAY_MS)
  }, [clearTimer, commit])

  const onRowEnter = useCallback(
    (index: number, event: { clientX: number; clientY: number }) => {
      const position = { x: event.clientX, y: event.clientY }
      if (isOnCourse(position)) {
        pendingRef.current = { index, position }
        armTimer()
        return
      }
      commit(index, position)
    },
    [armTimer, commit, isOnCourse]
  )

  const onListPointerMove = useCallback(
    (event: { clientX: number; clientY: number }) => {
      const position = { x: event.clientX, y: event.clientY }
      const pending = pendingRef.current
      const rect = panelRef.current?.getBoundingClientRect()
      if (pending && rect && position.x >= rect.left) {
        // Arrived over the panel (via something drawn on top of it): the
        // row the panel already shows is the one that was aimed at.
        cancel()
        return
      }
      if (pending) {
        pending.position = position
        if (isOnCourse(position)) armTimer()
        else commit(pending.index, position)
      }
      const trail = trailRef.current
      trail.push(position)
      if (trail.length > TRAIL_LENGTH) trail.shift()
    },
    [armTimer, cancel, commit, isOnCourse, panelRef]
  )

  return { onRowEnter, onListPointerMove, cancel }
}
